import {
  Autocomplete,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material';
import {
  CustomField,
  CustomFieldType,
  CustomFieldValidation,
} from '../GeneratedClient';
import {
  type CustomFieldValue,
  formatDateOnlyForApi,
  formatDateTimeForApi,
  parseDateOnlyApiValue,
  parseDateTimeApiValue,
} from './customFieldValue';
import { ValidateDatePicker } from './Forms/ValidateDatePicker';
import { sortByPolicyOrder } from './sortByPolicyOrder';

type CustomFieldInputProps = {
  customFieldPolicy: CustomField;
  value: CustomFieldValue;
  onChange: (value: CustomFieldValue) => void;
  multilineText?: boolean;
};

export function CustomFieldInput({
  customFieldPolicy,
  value,
  onChange,
  multilineText = false,
}: CustomFieldInputProps) {
  const type = customFieldPolicy.type!;

  if (type === CustomFieldType.Boolean) {
    return (
      <RadioGroup
        row
        value={value == null ? '' : value === true ? 'yes' : 'no'}
        onChange={(e) =>
          onChange(
            e.target.value === 'yes'
              ? true
              : e.target.value === 'no'
                ? false
                : null
          )
        }
      >
        <FormControlLabel value="yes" control={<Radio />} label="Yes" />
        <FormControlLabel value="no" control={<Radio />} label="No" />
        <FormControlLabel value="" control={<Radio />} label="(blank)" />
      </RadioGroup>
    );
  }

  if (type === CustomFieldType.StringArray) {
    const arrayValue = Array.isArray(value) ? value : [];
    const validValues = customFieldPolicy.validValues || [];
    return (
      <Autocomplete
        multiple
        options={validValues}
        value={sortByPolicyOrder(arrayValue, validValues)}
        onChange={(_, newValue) =>
          onChange(
            newValue.length ? sortByPolicyOrder(newValue, validValues) : null
          )
        }
        freeSolo={
          customFieldPolicy.validation === CustomFieldValidation.SuggestOnly
        }
        renderInput={(params) => <TextField {...params} />}
      />
    );
  }

  if (type === CustomFieldType.DateOnly) {
    return (
      <ValidateDatePicker
        value={parseDateOnlyApiValue(value)}
        onChange={(date) => onChange(date ? formatDateOnlyForApi(date) : null)}
      />
    );
  }

  if (type === CustomFieldType.DateTime) {
    return (
      <ValidateDatePicker
        includeTime
        value={parseDateTimeApiValue(value)}
        onChange={(date) => onChange(date ? formatDateTimeForApi(date) : null)}
      />
    );
  }

  if (customFieldPolicy.validation === CustomFieldValidation.SuggestOnly) {
    return (
      <Autocomplete
        freeSolo
        options={(customFieldPolicy.validValues || [])
          .slice()
          .sort((a, b) => -b.localeCompare(a))}
        inputValue={(value as string) || ''}
        onInputChange={(_, newValue, reason) => {
          if (reason === 'reset') return;

          onChange(newValue.length ? newValue : null);
        }}
        renderInput={(params) => <TextField {...params} />}
      />
    );
  }

  return (
    <TextField
      fullWidth={multilineText}
      minRows={multilineText ? 4 : undefined}
      multiline={multilineText}
      variant="outlined"
      size="medium"
      value={(value as string) || ''}
      onChange={(e) => onChange(e.target.value)}
      sx={
        multilineText
          ? {
              '& .MuiInputBase-inputMultiline': {
                minHeight: '6rem',
                resize: 'vertical',
              },
            }
          : undefined
      }
    />
  );
}
